import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Table, 
  Breadcrumb, 
  Tabs, 
  Select, 
  DatePicker, 
  Button,
  Space,
  Progress,
  Tag,
  Tooltip,
  Radio
} from 'antd';
import {
  UserOutlined,
  RiseOutlined,
  ShoppingOutlined,
  TeamOutlined,
  TrophyOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  ReloadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// 模拟的员工获客排行数据
interface EmployeeAcquisitionData {
  key: string;
  name: string;
  department: string;
  newCustomers: number;
  conversionRate: number;
  trend: 'up' | 'down' | 'stable';
  rank: number;
}

// 模拟的佣金排名数据
interface CommissionRankingData {
  key: string;
  name: string;
  department: string;
  commission: number;
  completionRate: number;
  salesAmount: number;
  rank: number;
}

// 模拟的客户复购率数据
interface RepurchaseRateData {
  key: string;
  name: string;
  department: string;
  customerCount: number;
  repurchaseRate: number;
  retentionRate: number;
  avgSpending: number;
}

const employeeAcquisitionData: EmployeeAcquisitionData[] = [
  {
    key: '1',
    name: '张三',
    department: '客房部',
    newCustomers: 28,
    conversionRate: 42.5,
    trend: 'up',
    rank: 1,
  },
  {
    key: '2',
    name: '李四',
    department: '餐饮部',
    newCustomers: 24,
    conversionRate: 38.2,
    trend: 'up',
    rank: 2,
  },
  {
    key: '3',
    name: '王五',
    department: '客房部',
    newCustomers: 20,
    conversionRate: 35.7,
    trend: 'down',
    rank: 3,
  },
  {
    key: '4',
    name: '赵六',
    department: '前厅部',
    newCustomers: 18,
    conversionRate: 30.2,
    trend: 'stable',
    rank: 4,
  },
  {
    key: '5',
    name: '钱七',
    department: '餐饮部',
    newCustomers: 15,
    conversionRate: 28.5,
    trend: 'up',
    rank: 5,
  },
];

const commissionRankingData: CommissionRankingData[] = [
  {
    key: '1',
    name: '李四',
    department: '餐饮部',
    commission: 8520,
    completionRate: 95,
    salesAmount: 156000,
    rank: 1,
  },
  {
    key: '2',
    name: '张三',
    department: '客房部',
    commission: 7650,
    completionRate: 88,
    salesAmount: 142000,
    rank: 2,
  },
  {
    key: '3',
    name: '王五',
    department: '客房部',
    commission: 6800,
    completionRate: 78,
    salesAmount: 125000,
    rank: 3,
  },
  {
    key: '4',
    name: '钱七',
    department: '餐饮部',
    commission: 5600,
    completionRate: 65,
    salesAmount: 98000,
    rank: 4,
  },
  {
    key: '5',
    name: '赵六',
    department: '前厅部',
    commission: 4200,
    completionRate: 52,
    salesAmount: 75000,
    rank: 5,
  },
];

const repurchaseRateData: RepurchaseRateData[] = [
  {
    key: '1',
    name: '张三',
    department: '客房部',
    customerCount: 48,
    repurchaseRate: 65.2,
    retentionRate: 78.3,
    avgSpending: 3200,
  },
  {
    key: '2',
    name: '李四',
    department: '餐饮部',
    customerCount: 32,
    repurchaseRate: 72.4,
    retentionRate: 83.6,
    avgSpending: 2800,
  },
  {
    key: '3',
    name: '王五',
    department: '客房部',
    customerCount: 27,
    repurchaseRate: 58.6,
    retentionRate: 70.2,
    avgSpending: 3500,
  },
  {
    key: '4',
    name: '赵六',
    department: '前厅部',
    customerCount: 15,
    repurchaseRate: 45.8,
    retentionRate: 62.4,
    avgSpending: 2100,
  },
  {
    key: '5',
    name: '钱七',
    department: '餐饮部',
    customerCount: 22,
    repurchaseRate: 59.5,
    retentionRate: 71.8,
    avgSpending: 2500,
  },
];

const DataMonitoringDashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<string>('month');
  
  // 获客排行表格列定义
  const acquisitionColumns: ColumnsType<EmployeeAcquisitionData> = [
    {
      title: '排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank) => (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          width: 30, 
          height: 30, 
          borderRadius: '50%', 
          background: rank <= 3 ? '#529C8A' : '#f0f0f0',
          color: rank <= 3 ? '#fff' : '#000',
          fontWeight: 'bold'
        }}>
          {rank}
        </div>
      )
    },
    {
      title: '员工姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: '新增客户数',
      dataIndex: 'newCustomers',
      key: 'newCustomers',
      sorter: (a, b) => a.newCustomers - b.newCustomers,
    },
    {
      title: '转化率',
      dataIndex: 'conversionRate',
      key: 'conversionRate',
      render: (rate) => `${rate}%`,
      sorter: (a, b) => a.conversionRate - b.conversionRate,
    },
    {
      title: '趋势',
      dataIndex: 'trend',
      key: 'trend',
      render: (trend) => (
        <span>
          {trend === 'up' && <CaretUpOutlined style={{ color: '#52c41a' }} />}
          {trend === 'down' && <CaretDownOutlined style={{ color: '#f5222d' }} />}
          {trend === 'stable' && <span>-</span>}
        </span>
      ),
    },
  ];

  // 佣金排名表格列定义
  const commissionColumns: ColumnsType<CommissionRankingData> = [
    {
      title: '排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank) => (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          width: 30, 
          height: 30, 
          borderRadius: '50%', 
          background: rank <= 3 ? '#529C8A' : '#f0f0f0',
          color: rank <= 3 ? '#fff' : '#000',
          fontWeight: 'bold'
        }}>
          {rank}
        </div>
      )
    },
    {
      title: '员工姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: '佣金(元)',
      dataIndex: 'commission',
      key: 'commission',
      render: (amount) => `¥${amount.toLocaleString()}`,
      sorter: (a, b) => a.commission - b.commission,
    },
    {
      title: '业绩完成率',
      dataIndex: 'completionRate',
      key: 'completionRate',
      render: (rate) => (
        <Tooltip title={`${rate}%`}>
          <Progress percent={rate} size="small" strokeColor="#529C8A" />
        </Tooltip>
      ),
      sorter: (a, b) => a.completionRate - b.completionRate,
    },
    {
      title: '销售额(元)',
      dataIndex: 'salesAmount',
      key: 'salesAmount',
      render: (amount) => `¥${amount.toLocaleString()}`,
      sorter: (a, b) => a.salesAmount - b.salesAmount,
    },
  ];

  // 客户复购率表格列定义
  const repurchaseColumns: ColumnsType<RepurchaseRateData> = [
    {
      title: '员工姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: '绑定客户数',
      dataIndex: 'customerCount',
      key: 'customerCount',
      sorter: (a, b) => a.customerCount - b.customerCount,
    },
    {
      title: '复购率',
      dataIndex: 'repurchaseRate',
      key: 'repurchaseRate',
      render: (rate) => (
        <Tooltip title={`${rate}%`}>
          <Progress percent={rate} size="small" strokeColor="#529C8A" />
        </Tooltip>
      ),
      sorter: (a, b) => a.repurchaseRate - b.repurchaseRate,
    },
    {
      title: '客户留存率',
      dataIndex: 'retentionRate',
      key: 'retentionRate',
      render: (rate) => `${rate}%`,
      sorter: (a, b) => a.retentionRate - b.retentionRate,
    },
    {
      title: '客单价(元)',
      dataIndex: 'avgSpending',
      key: 'avgSpending',
      render: (amount) => `¥${amount.toLocaleString()}`,
      sorter: (a, b) => a.avgSpending - b.avgSpending,
    },
  ];

  return (
    <div className="data-monitoring-dashboard">
      <Breadcrumb 
        style={{ marginBottom: 16 }} 
        items={[
          { title: '首页' },
          { title: '数据监控' },
        ]}
      />
      
      <div style={{ padding: '16px', background: '#fff' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Radio.Group 
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              style={{ marginRight: 16 }}
            >
              <Radio.Button value="week">本周</Radio.Button>
              <Radio.Button value="month">本月</Radio.Button>
              <Radio.Button value="quarter">本季度</Radio.Button>
              <Radio.Button value="year">本年度</Radio.Button>
            </Radio.Group>
            <RangePicker style={{ marginRight: 16 }} />
          </div>
          <Space>
            <Button icon={<ReloadOutlined />}>刷新</Button>
            <Button icon={<DownloadOutlined />}>导出数据</Button>
          </Space>
        </div>
        
        {/* 关键指标概览 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总佣金金额"
                value={32768}
                precision={2}
                valueStyle={{ color: '#529C8A' }}
                prefix="¥"
                suffix={<Tooltip title="同比增长15.2%"><RiseOutlined style={{ fontSize: 16 }} /></Tooltip>}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="新增客户"
                value={105}
                valueStyle={{ color: '#1890ff' }}
                prefix={<UserOutlined />}
                suffix={<Tooltip title="同比增长8.5%"><RiseOutlined style={{ fontSize: 16 }} /></Tooltip>}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总销售额"
                value={596000}
                precision={2}
                valueStyle={{ color: '#faad14' }}
                prefix="¥"
                suffix={<Tooltip title="同比增长23.7%"><RiseOutlined style={{ fontSize: 16 }} /></Tooltip>}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均复购率"
                value={58.6}
                precision={1}
                valueStyle={{ color: '#722ed1' }}
                suffix="%"
                prefix={<ShoppingOutlined />}
              />
            </Card>
          </Col>
        </Row>
        
        <Tabs defaultActiveKey="1">
          <TabPane tab="员工获客排行" key="1" style={{ marginBottom: 16 }}>
            <Table 
              columns={acquisitionColumns} 
              dataSource={employeeAcquisitionData}
              pagination={false}
              rowKey="key"
            />
          </TabPane>
          
          <TabPane tab="佣金排名" key="2">
            <Table 
              columns={commissionColumns} 
              dataSource={commissionRankingData}
              pagination={false}
              rowKey="key"
            />
          </TabPane>
          
          <TabPane tab="客户复购率分析" key="3">
            <Table 
              columns={repurchaseColumns} 
              dataSource={repurchaseRateData}
              pagination={false}
              rowKey="key"
            />
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default DataMonitoringDashboard; 